import { Trash2, Circle, AlertTriangle } from 'lucide-react';
import { useAlerts, HealthAlert } from '../../contexts/AlertsContext';
import { useCopilotContext } from '../../contexts/CopilotContext';

interface AlertCardProps {
  alert: HealthAlert;
  onDelete: (id: string) => void;
  onAnalyze: (id: string) => void;
}

function AlertCard({ alert, onDelete, onAnalyze }: AlertCardProps) {
  const isCapability = alert.type === 'capability';
  const titleColor = isCapability ? 'text-orange-600' : 'text-blue-600';
  const iconBgColor = isCapability ? 'bg-orange-100' : 'bg-blue-100';
  const iconColor = isCapability ? 'text-orange-600' : 'text-blue-600';

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-2 flex-1">
          <div className={`p-1.5 rounded ${iconBgColor}`}>
            <AlertTriangle className={`h-4 w-4 ${iconColor}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className={`font-semibold text-sm ${titleColor}`}>
              {alert.title}
            </h4>
            <p className="text-sm text-gray-700 mt-1 truncate">
              {alert.nodeName}
            </p>
            <p className="text-sm text-gray-500 mt-0.5">
              {Math.round(alert.health)}%
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={() => onAnalyze(alert.id)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Analyze with AI"
            title="Analyze with AI"
          >
            <Circle className="h-4 w-4" />
          </button>
          <button
            onClick={() => onDelete(alert.id)}
            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-red-600 transition-colors"
            aria-label="Delete Notification"
            title="Delete Notification"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface AlertsPanelProps {
  onClose: () => void;
  onOpenCopilot?: () => void;
}

export function AlertsPanel({ onClose, onOpenCopilot }: AlertsPanelProps) {
  const { alerts, removeAlert, searchApi } = useAlerts();
  const { setPendingMessage } = useCopilotContext();

  const handleDelete = (id: string) => {
    removeAlert(id);
  };

  const handleAnalyze = (id: string) => {
    const alertItem = alerts.find(a => a.id === id);
    if (!alertItem) return;

    const message = `Please explain the reason for this health alert: "${alertItem.title}" for ${alertItem.type} "${alertItem.nodeName}" which has a health score of ${Math.round(alertItem.health)}%. What could be causing this low health score and what actions might improve it?`;
    
    // Zoom to the node in the graph
    if (searchApi) {
      if (alertItem.type === 'stage') {
        searchApi.zoomStageHeader(alertItem.nodeName);
      } else {
        searchApi.search(alertItem.nodeName);
      }
    }
    
    // Close notifications panel
    onClose();
    
    // Open copilot panel
    onOpenCopilot?.();
    
    // Send message to copilot
    setPendingMessage(message);
  };

  return (
    <div className="fixed right-0 w-96 bg-white border-l shadow-lg z-50 flex flex-col" style={{ height: 'calc(100vh - 96px)' }}>
      <div className="flex items-center justify-between px-4 py-3 border-b bg-gray-50">
        <h3 className="font-semibold text-sm">Notifications ({alerts.length})</h3>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-gray-700 p-1 rounded hover:bg-gray-200"
          aria-label="Close notifications"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-2">
        {alerts.length === 0 ? (
          <div className="text-center text-gray-500 py-8">
            <p className="text-sm">No notifications</p>
          </div>
        ) : (
          alerts.map((alert) => (
            <AlertCard
              key={alert.id}
              alert={alert}
              onDelete={handleDelete}
              onAnalyze={handleAnalyze}
            />
          ))
        )}
      </div>
    </div>
  );
}
